import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';
import { Mail, AlertTriangle, Trash2, RefreshCw } from 'lucide-react';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

function Inbox({ token, me, onLogout }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isSuperadmin = me?.role === 'superadmin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/contact-enquiries`, { headers: authHeaders(token) });
      setEnquiries(data);
      if (data.length && !data.find((d) => d.id === selectedId)) {
        setSelectedId(data[0].id);
      }
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError('Failed to load contact enquiries.');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, selectedId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      e.name.toLowerCase().includes(q)
      || e.email.toLowerCase().includes(q)
      || e.subject.toLowerCase().includes(q)
      || e.message.toLowerCase().includes(q),
    );
  }, [enquiries, query]);

  const current = useMemo(() => enquiries.find((e) => e.id === selectedId) || null, [enquiries, selectedId]);
  const failedCount = useMemo(() => enquiries.filter((e) => !e.email_sent).length, [enquiries]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this enquiry? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/contact-enquiries/${id}`, { headers: authHeaders(token) });
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      setSelectedId(null);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError('Failed to delete enquiry.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-[#F4F4F5] min-h-screen py-10 px-4 sm:px-6 lg:px-8" data-testid="admin-contact-inbox">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">
              Admin Dashboard
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">Contact Inbox</h1>
            <p className="text-sm text-[#52525B] mt-2" data-testid="admin-contact-counts">
              {enquiries.length} total
              {failedCount > 0 && (
                <>
                  {' '}&middot; <span className="text-[#D62F2F] font-semibold">{failedCount} not emailed</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-contact-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {failedCount > 0 && (
          <div
            data-testid="admin-contact-failed-banner"
            className="bg-[#0A0A0A] text-white p-4 border-l-4 border-[#D62F2F] mb-6 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-[#D62F2F] mt-0.5 flex-shrink-0" />
            <p className="text-sm leading-relaxed">
              <strong>{failedCount}</strong> {failedCount === 1 ? 'enquiry was not emailed' : 'enquiries were not emailed'} to info@. The submissions are safe in the database — reach out manually below.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 text-sm text-red-900">{error}</div>
        )}

        <div className="mb-5">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, subject or message…"
            data-testid="admin-contact-search"
          />
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500" data-testid="admin-contact-loading">Loading…</div>
        ) : enquiries.length === 0 ? (
          <div
            data-testid="admin-contact-empty"
            className="bg-white border border-zinc-200 p-10 text-center text-sm text-[#52525B]"
          >
            <Mail className="w-10 h-10 text-[#F0A500] mx-auto mb-3" />
            No contact enquiries yet. Submissions from the public <code>/contact</code> page will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* Enquiry list */}
            <div className="lg:col-span-2 bg-white border border-zinc-200 max-h-[70vh] overflow-y-auto">
              {filtered.length === 0 && (
                <div className="p-6 text-sm text-zinc-500">No enquiries match your search.</div>
              )}
              {filtered.map((e) => {
                const active = e.id === selectedId;
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelectedId(e.id)}
                    data-testid={`admin-contact-row-${e.id}`}
                    className={`w-full text-left p-4 border-b border-zinc-100 transition-colors ${
                      active ? 'bg-[#FEF7E6] border-l-4 border-l-[#F0A500]' : 'hover:bg-zinc-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-bold text-[#0A0A0A] truncate">{e.name}</span>
                      {!e.email_sent && (
                        <span
                          data-testid={`admin-contact-row-flag-${e.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold text-white bg-[#D62F2F] px-2 py-0.5 whitespace-nowrap"
                        >
                          Not emailed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#52525B] truncate mb-1.5">{e.email}</div>
                    <div className="text-sm font-semibold text-[#0A0A0A] truncate">{e.subject}</div>
                    <div className="text-xs text-zinc-500 mt-1">{formatDate(e.submitted_at)}</div>
                  </button>
                );
              })}
            </div>

            {/* Detail pane */}
            <div className="lg:col-span-3 bg-white border border-zinc-200">
              {current ? (
                <div className="p-6 sm:p-8" data-testid="admin-contact-detail">
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#0A0A0A]">{current.subject}</h2>
                      <p className="text-sm text-[#52525B] mt-1">
                        From <strong>{current.name}</strong> &middot;{' '}
                        <a
                          href={`mailto:${current.email}?subject=Re:%20${encodeURIComponent(current.subject)}`}
                          data-testid="admin-contact-reply-link"
                          className="text-[#F0A500] hover:text-[#D99500] font-semibold"
                        >
                          {current.email}
                        </a>
                      </p>
                      {current.phone && (
                        <p className="text-sm text-[#52525B] mt-1">Phone: <strong>{current.phone}</strong></p>
                      )}
                      <p className="text-xs text-zinc-500 mt-2" data-testid="admin-contact-detail-timestamp">
                        Submitted {formatDate(current.submitted_at)}
                        {current.ip && (
                          <>
                            {' '}&middot; <span className="font-mono">{current.ip}</span>
                          </>
                        )}
                      </p>
                    </div>
                    {isSuperadmin && (
                      <button
                        type="button"
                        onClick={() => handleDelete(current.id)}
                        disabled={deleting}
                        data-testid="admin-contact-delete-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#D62F2F] hover:text-white hover:bg-[#D62F2F] border border-[#D62F2F] transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    )}
                  </div>

                  {!current.email_sent && (
                    <div
                      data-testid="admin-contact-detail-failed"
                      className="bg-[#FEF2F2] border-l-4 border-[#D62F2F] p-4 mb-5 text-sm text-[#991B1B]"
                    >
                      <strong>Not emailed to info@.</strong> This submission is saved but Maileroo didn&rsquo;t accept it — reply directly via the mailto link above.
                    </div>
                  )}

                  <div
                    data-testid="admin-contact-detail-message"
                    className="whitespace-pre-wrap text-sm text-[#0A0A0A] leading-relaxed bg-[#FAFAF9] border border-zinc-200 p-5"
                  >
                    {current.message}
                  </div>

                  <a
                    href={`mailto:${current.email}?subject=Re:%20${encodeURIComponent(current.subject)}&body=${encodeURIComponent(`\n\n--- Original message ---\n${current.message}`)}`}
                    data-testid="admin-contact-reply-btn"
                    className="mt-6 inline-block px-5 py-2.5 bg-[#F0A500] hover:bg-[#D99500] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs"
                  >
                    Reply via email &rarr;
                  </a>
                </div>
              ) : (
                <div className="p-10 text-center text-sm text-zinc-500">Select an enquiry to view the full message.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminContactEnquiriesPage() {
  const { token, me, checking, loginSuccess, logout } = useAdminAuth();
  if (checking) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  if (!token || !me) {
    return (
      <div data-testid="admin-contact-page">
        <SEO title="Admin Contact Inbox" description="Restricted admin area." path="/admin/contact-enquiries" noIndex />
        <AdminLoginGate title="Contact Inbox" subtitle="Sign in to review contact-form submissions." onLoggedIn={loginSuccess} />
      </div>
    );
  }
  return (
    <div data-testid="admin-contact-page">
      <SEO title="Admin Contact Inbox" description="Restricted admin area." path="/admin/contact-enquiries" noIndex />
      <AdminTabs current="contact" me={me} onLogout={logout} />
      <Inbox token={token} me={me} onLogout={logout} />
    </div>
  );
}
